import { get, isEmpty } from 'lodash';

import { brandModuleCodes } from '../containers/Rewards/config';
import { checkBrandModuleApplicable } from '../containers/Rewards/helpers';
import orderingRoutes from '../routes/ordering';
import rewardsRoutes from '../routes/rewards';
import siteRoutes from '../routes/site';
import { isNativeWebView } from './helpers';

export const FUDR_BOOT_GLOBAL_KEY = '__FUDR_BOOT__';

export const FUDR_SURFACES = {
  SITE: 'site',
  APP: 'app',
  ORDERING: 'ordering',
  LEGACY: 'legacy',
};

/** Shell-shaped appStartup body used for prodServer inject + Site/Rewards parity. */
export const FUDR_BOOT_INCLUDE_MODULES = ['reward', 'brand', 'resource', 'whiteLabelApp'];

/**
 * Read window.__FUDR_BOOT__ injected by prodServer.
 * Returns null when missing/invalid (CRA / failed server startup).
 */
export const readFudrBoot = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return get(window, FUDR_BOOT_GLOBAL_KEY, null);
};

/**
 * Boot is usable when it carries appStartupInfo with brandId or modules.
 */
export const isValidFudrBoot = (boot) => {
  if (!boot || typeof boot !== 'object') {
    return false;
  }
  const appStartupInfo = get(boot, 'appStartupInfo', null);
  if (!appStartupInfo || typeof appStartupInfo !== 'object') {
    return false;
  }
  const brandId = get(appStartupInfo, 'brandId', '');
  const modules = get(boot, 'modules', get(appStartupInfo, 'modules', []));
  return !isEmpty(brandId) || !isEmpty(modules);
};

export const getBootModules = (boot) => {
  if (!boot) {
    return [];
  }
  const fromBoot = get(boot, 'modules', []);
  if (!isEmpty(fromBoot)) {
    return fromBoot;
  }
  return get(boot, 'appStartupInfo.modules', []) || [];
};

const isWebsiteEnabled = (modules = []) =>
  checkBrandModuleApplicable({
    applicableBrandModules: modules,
    moduleName: brandModuleCodes.website,
  });

const isRewardEnabled = (modules = []) =>
  checkBrandModuleApplicable({
    applicableBrandModules: modules,
    moduleName: brandModuleCodes.reward,
  });

/**
 * Single source of truth for surface (site | app | ordering | legacy).
 *
 * No whitelabel subdomain → always legacy (path must not promote to site/app/ordering).
 * With subdomain: path wins for shells; modules fill root; native never site.
 * onlineOrder never forces ordering — only /ordering path on a whitelabel host.
 */
export const resolveSurface = ({
  pathname = '',
  modules = [],
  isNative = isNativeWebView,
  hasWhitelabelSubdomain = true,
} = {}) => {
  if (!hasWhitelabelSubdomain) {
    return FUDR_SURFACES.LEGACY;
  }

  const path = String(pathname || '').split('?')[0];
  const firstSegment = path.split('/').filter(Boolean)[0];
  const orderingRoot = `/${orderingRoutes.ordering}`;

  if (path === orderingRoot || path.startsWith(`${orderingRoot}/`)) {
    return FUDR_SURFACES.ORDERING;
  }
  if (firstSegment === rewardsRoutes.rewards) {
    // Native always uses the app shell under /app.
    if (isNative) {
      return FUDR_SURFACES.APP;
    }
    // Browser + website: keep site surface so only /app/scan is allowed (not full /app).
    if (isWebsiteEnabled(modules)) {
      return FUDR_SURFACES.SITE;
    }
    return FUDR_SURFACES.APP;
  }
  if (firstSegment === siteRoutes.site) {
    return isNative ? FUDR_SURFACES.APP : FUDR_SURFACES.SITE;
  }

  if (isWebsiteEnabled(modules) && !isNative) {
    return FUDR_SURFACES.SITE;
  }
  if (isRewardEnabled(modules) || (isWebsiteEnabled(modules) && isNative)) {
    return FUDR_SURFACES.APP;
  }
  return FUDR_SURFACES.LEGACY;
};

/**
 * Set Redux surface from the destination path, then navigate.
 * Use on surface hops (e.g. /app/scan → /ordering) so common.surface flips
 * before the shell remounts — avoids one frame of wrong ThemeContext/router.
 */
export const commitSurfaceNavigation = ({
  pathname,
  modules = [],
  dispatchSurface,
  navigate,
  hasWhitelabelSubdomain = true,
}) => {
  const pathOnly = String(pathname || '').split('?')[0];
  const surface = resolveSurface({ pathname: pathOnly, modules, hasWhitelabelSubdomain });
  if (typeof dispatchSurface === 'function') {
    dispatchSurface(surface);
  }
  if (typeof navigate === 'function') {
    navigate(pathname);
  }
  return surface;
};

/** Cold-load hint for __FUDR_BOOT__ (same rules; Node passes isNative: false). */
export const getBootSurface = (
  pathname = '',
  modules = [],
  isNative = isNativeWebView,
  hasWhitelabelSubdomain = true,
) => resolveSurface({ pathname, modules, isNative, hasWhitelabelSubdomain });

/**
 * True when Redux already has shell-class startup (brand or resources) from boot/fetch.
 * Site/Rewards use this to skip their second appStartup.
 */
export const hasShellStartupData = (appStartupInfo = {}) => {
  if (isEmpty(appStartupInfo)) {
    return false;
  }
  const brandId = get(appStartupInfo, 'brandId', '');
  const hasBrand = !isEmpty(get(appStartupInfo, 'brand', {}));
  const hasResource = !isEmpty(get(appStartupInfo, 'brandResource', {}));
  return !isEmpty(brandId) && (hasBrand || hasResource);
};

/** Build inject payload from an appStartup API data object (server-side / tests). */
export const buildFudrBootPayload = (
  appStartupInfo = {},
  pathname = '',
  { isNative = false, hasWhitelabelSubdomain = true } = {},
) => {
  const modules = get(appStartupInfo, 'modules', []) || [];
  return {
    surface: resolveSurface({ pathname, modules, isNative, hasWhitelabelSubdomain }),
    appStartupInfo,
    modules,
    pageGraph: get(appStartupInfo, 'pageGraph', null),
  };
};

/**
 * The brand's page graph, from Redux first and the injected boot as fallback.
 *
 * Absent, null and empty all mean the same thing downstream (legacy routing), so
 * this returns null rather than distinguishing them.
 */
export const readPageGraph = ({ appStartupInfo = {}, boot = readFudrBoot() } = {}) =>
  get(appStartupInfo, 'pageGraph', null) ||
  get(boot, 'pageGraph', null) ||
  get(boot, 'appStartupInfo.pageGraph', null) ||
  null;
